import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import type { MyGroupsDailySpendResponse, MyGroupsResponse } from "../../api/organisationReporting";
import { formatDate, formatMoney, formatWholeMoney } from "../consumption/formatters";
import { VendorIcon } from "../consumption/VendorIcon";
import heroStyles from "../consumption/UsageHero.module.css";
import card from "../tools/ToolCard.module.css";
import { computeGroupPace, type GroupStatus } from "./groupPace";
import { computeGroupsOverview } from "./groupsOverview";
import { statusText, statusTones } from "./groupText";
import { PeopleLimitChips } from "./PeopleLimitChips";
import styles from "./MyGroupsHero.module.css";

/** How many groups the attention list names before it summarises the rest. */
const AttentionLimit = 5;

/**
 * The top of "My Groups", in the style of the Organisation page's hero, for a manager with several teams: what their groups
 * have spent between them and where the month is heading, which groups need a look (over, heading over or close to their
 * allowance), and a few facts that compare the groups. The figures come from the outermost groups, so a department and
 * the groups inside it are never added together.
 *
 * @param props.response - The loaded "my groups" response.
 * @param props.dailySpend - Each group's daily series, for the projections; null until it arrives or when it is unavailable.
 * @param props.isCurrentMonth - Whether the period is the current month; a past month has no projection.
 */
export function MyGroupsHero({
  response,
  dailySpend,
  isCurrentMonth,
}: {
  response: MyGroupsResponse;
  dailySpend: MyGroupsDailySpendResponse | null;
  isCurrentMonth: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const currency = response.currency;
  const overview = computeGroupsOverview(response, dailySpend, isCurrentMonth);
  const { spend, projected } = overview;
  // The pace gives today's place in the month; the allowances are judged group by group, in the attention list.
  const pace = computeGroupPace(spend ?? 0, null, projected, overview.people, response, isCurrentMonth);
  const showMeter = spend !== null && projected !== null && projected > 0;
  const spentPercent = showMeter ? Math.min(100, ((spend ?? 0) / projected) * 100) : 0;
  const [onlyRoot] = overview.roots;
  const shownAttention = overview.attention.slice(0, AttentionLimit);
  const moreAttention = overview.attention.length - shownAttention.length;
  const anyDanger = overview.attention.some((item) => item.tone === "danger");
  const hasPeopleNews = overview.peopleAtLimit > 0 || overview.peopleNearLimit > 0;

  return (
    <section className={heroStyles.hero} aria-label={t("myGroups.hero.label")} data-spotlight>
      <div className={heroStyles.main}>
        <div className={heroStyles.headerRow}>
          <p className={`panel-title ${heroStyles.eyebrow}`}>{t("myGroups.hero.eyebrow")}</p>
          <p className={heroStyles.periodRange}>
            {t("consumption.period.range", { start: formatDate(response.periodStart), end: formatDate(response.periodEnd) })}
          </p>
        </div>

        <p className={heroStyles.amount}>
          <span>{spend !== null ? formatMoney(spend, currency) : t("myGroups.notYetAvailable")}</span>
        </p>

        {showMeter && (
          <div className={heroStyles.meter}>
            <div
              className={heroStyles.track}
              role="meter"
              aria-label={t("organisation.hero.meterLabel")}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(spentPercent)}
              aria-valuetext={t("organisation.hero.meterValue", { percent: Math.round(spentPercent) })}
            >
              <div className={heroStyles.projectedFill} style={{ width: "100%" }} />
              <div className={heroStyles.usedFill} style={{ width: `${String(spentPercent)}%` }} />
              <div className={heroStyles.todayMarker} style={{ left: `${String(pace.percentElapsed)}%` }} />
            </div>
            <div className={heroStyles.meterKey} aria-hidden="true">
              <span className={heroStyles.keyItem}>
                <span className={`${heroStyles.keySwatch} ${heroStyles.keyUsed}`} />
                {t("organisation.hero.spentSoFar")}
              </span>
              <span className={heroStyles.keyItem}>
                <span className={`${heroStyles.keySwatch} ${heroStyles.keyProjected}`} />
                {t("consumption.hero.meterProjected")}
              </span>
              <span className={heroStyles.keyItem}>
                <span className={heroStyles.keyToday} />
                {t("consumption.hero.meterToday")}
              </span>
            </div>
          </div>
        )}

        {spend !== null && (
          <p className={heroStyles.summary}>
            {projected !== null
              ? t("organisation.hero.pace", {
                  spent: formatWholeMoney(spend, currency),
                  elapsed: Math.round(pace.percentElapsed),
                  projected: formatWholeMoney(projected, currency),
                })
              : t("organisation.hero.past", { spent: formatWholeMoney(spend, currency) })}
          </p>
        )}
        <p className={heroStyles.note}>
          {overview.roots.length === 1
            ? t("myGroups.hero.scopeOne", { name: onlyRoot.groupLabel, count: overview.groupCount - 1 })
            : t("myGroups.hero.scopeMany", { count: overview.roots.length })}
          {!overview.wasFullyConverted && ` ${t("myGroups.notFullyConverted")}`}
        </p>

        {(overview.withAllowanceCount > 0 || shownAttention.length > 0) &&
          (shownAttention.length > 0 ? (
            // Red when a group is over or heading over its allowance, or someone has reached a limit; amber when the worst is only close.
            <div className={`${styles.attention} ${anyDanger ? styles.attentionDanger : styles.attentionWarning}`} role="group" aria-labelledby="my-groups-attention-title">
              <p id="my-groups-attention-title" className={styles.attentionTitle}>
                <WarningIcon />
                {t("myGroups.hero.attentionTitle", { count: overview.attention.length })}
              </p>
              {hasPeopleNews && (
                <p className={styles.attentionPeople}>
                  {[
                    overview.peopleAtLimit > 0 ? t("myGroups.limits.reached", { count: overview.peopleAtLimit }) : null,
                    overview.peopleNearLimit > 0 ? t("myGroups.limits.near", { count: overview.peopleNearLimit }) : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              )}
              <ul className={styles.attentionList}>
                {shownAttention.map((item) => (
                  <li key={item.group.groupId} className={styles.attentionItem}>
                    {/* The parent tells same-named groups apart, as on the cards. */}
                    <span className={styles.attentionName}>
                      {/* The link covers the whole row, so any part of it opens the group; it stays the one link a screen reader hears. */}
                      <Link to={`/my-groups/${encodeURIComponent(item.group.groupId)}`} className={`${styles.attentionLink} ${styles.attentionItemLink}`}>
                        {item.group.groupLabel}
                      </Link>
                      {item.group.breadcrumbs.length > 0 && <span className={styles.attentionPath}>{item.group.breadcrumbs[item.group.breadcrumbs.length - 1]}</span>}
                    </span>
                    <span className={styles.attentionChips}>
                      {severityShown(item.pace.status) && <span className={`${card.chip} ${statusTones[item.pace.status]} ${styles.attentionChip}`}>{statusText(t, item.pace)}</span>}
                      <PeopleLimitChips reached={item.peopleAtLimit} near={item.peopleNearLimit} className={styles.attentionChip} />
                    </span>
                  </li>
                ))}
              </ul>
              {moreAttention > 0 && <p className={styles.attentionMore}>{t("myGroups.hero.attentionMore", { count: moreAttention })}</p>}
            </div>
          ) : (
            <p className={`${styles.attention} ${styles.attentionGood} ${styles.attentionTitle}`}>
              <CheckIcon />
              {t(isCurrentMonth ? "myGroups.hero.allOnTrack" : "myGroups.hero.allWithin")}
            </p>
          ))}
      </div>

      <dl className={heroStyles.stats} aria-label={t("myGroups.hero.statsLabel")}>
        {isCurrentMonth && (
          <div className={`${heroStyles.stat} ${heroStyles.statFeatured}`}>
            <dt>{t("consumption.hero.projectedLabel")}</dt>
            <dd>{projected !== null ? formatMoney(projected, currency) : t("myGroups.notYetAvailable")}</dd>
          </div>
        )}
        <div className={heroStyles.stat}>
          <dt>{t("myGroups.card.perPerson")}</dt>
          <dd>{overview.perPerson !== null ? formatMoney(overview.perPerson, currency) : "–"}</dd>
        </div>
        <div className={heroStyles.stat}>
          <dt>{t("myGroups.card.activePeople")}</dt>
          <dd>{overview.people.toLocaleString("en-US")}</dd>
        </div>
        {/* Two facts that name something, laid out across the row: what is named on the left, its figure on the right. */}
        {overview.topTool && (
          <div className={`${heroStyles.stat} ${styles.insight}`}>
            <dt className={styles.insightLabel}>
              <span className={styles.insightIcon} aria-hidden="true">
                <VendorIcon vendorId={overview.topTool.vendorId} className={styles.vendorIcon} />
              </span>
              <span className={styles.insightLabelText}>{t("myGroups.hero.topTool")}</span>
            </dt>
            <dd className={styles.insightName}>{overview.topTool.name}</dd>
            <dd className={styles.insightFigure}>
              {t("myGroups.hero.share", { share: overview.topTool.share })}
              <span className={heroStyles.statNote}>{t("myGroups.hero.ofSpend")}</span>
            </dd>
          </div>
        )}
        {overview.highestPerPerson && (
          <div className={`${heroStyles.stat} ${styles.insight}`}>
            <dt className={styles.insightLabel}>
              <span className={styles.insightIcon} aria-hidden="true">
                <PersonIcon />
              </span>
              <span className={styles.insightLabelText}>{t("myGroups.hero.highestPerPerson")}</span>
            </dt>
            <dd className={styles.insightName}>
              <Link to={`/my-groups/${encodeURIComponent(overview.highestPerPerson.group.groupId)}`} className={styles.attentionLink}>
                {overview.highestPerPerson.group.groupLabel}
              </Link>
            </dd>
            <dd className={styles.insightFigure}>
              {formatMoney(overview.highestPerPerson.perPerson, currency)}
              <span className={heroStyles.statNote}>{t("myGroups.hero.perActivePerson")}</span>
            </dd>
          </div>
        )}
      </dl>
    </section>
  );
}

/** Whether a group's own allowance status is news for the attention list: over, heading over or close to it. */
function severityShown(status: GroupStatus): boolean {
  return status === "over" || status === "heading" || status === "close";
}

/** A warning triangle, beside the groups that need a look. */
function WarningIcon(): React.JSX.Element {
  return (
    <svg className={styles.statusIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" />
    </svg>
  );
}

/** A tick in a circle, when every group is within its allowance. */
function CheckIcon(): React.JSX.Element {
  return (
    <svg className={styles.statusIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14M22 4 12 14.01l-3-3" />
    </svg>
  );
}

/** A person, beside the group whose people spend the most each. */
function PersonIcon(): React.JSX.Element {
  return (
    <svg className={styles.vendorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8" />
    </svg>
  );
}
